<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("platform_statement.detail") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>

                <div class="col-lg-6 col-5 text-right">
                    <base-button size="md" type="success" @click="handleExport">
                        <i class="fa fa-download mr-2"></i>{{ $t("platform_statement.export_excel") }}
                    </base-button>
                    <base-button size="md" type="secondary" @click="handleBack">
                        <i class="fa fa-arrow-left mr-2"></i>{{ $t("back") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <!-- Statement Header Info -->
            <card v-if="statement">
                <template slot="header">
                    <div class="d-flex justify-content-between align-items-center">
                        <h3 class="mb-0">{{ statement.statement_number }}</h3>
                        <badge type="success" v-if="statement.status === 1">Active</badge>
                    </div>
                </template>

                <div class="row">
                    <div class="col-md-3">
                        <base-input
                            :label="$t('platform_statement.platform')"
                            :value="getPlatformName(statement.platform_id)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-3">
                        <base-input
                            :label="$t('platform_statement.statement_date')"
                            :value="formatDate(statement.statement_date)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-3">
                        <base-input
                            :label="$t('platform_statement.settlement_date')"
                            :value="formatDate(statement.settlement_date)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-3">
                        <base-input
                            :label="$t('platform_statement.uploaded_by')"
                            :value="statement.uploaded_by_name || '-'"
                            disabled
                        ></base-input>
                    </div>
                </div>

                <div class="row mt-2">
                    <div class="col-md-6">
                        <base-input
                            :label="$t('platform_statement.uploaded_filename')"
                            :value="statement.uploaded_filename || '-'"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-3">
                        <base-input
                            label="Created At"
                            :value="formatDateTime(statement.created_at)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-3">
                        <base-input
                            label="Updated At"
                            :value="formatDateTime(statement.updated_at)"
                            disabled
                        ></base-input>
                    </div>
                </div>
            </card>

            <!-- Summary Statistics -->
            <div class="row" v-if="statement">
                <div class="col-lg-3 col-md-6">
                    <stats-card
                        :title="$t('platform_statement.total_orders')"
                        type="gradient-red"
                        :sub-title="formatNumber(statement.total_orders)"
                        icon="ni ni-bag-17"
                    >
                    </stats-card>
                </div>
                <div class="col-lg-3 col-md-6">
                    <stats-card
                        :title="$t('platform_statement.total_sales_amount')"
                        type="gradient-orange"
                        :sub-title="formatCurrency(statement.total_sales_amount)"
                        icon="ni ni-money-coins"
                    >
                    </stats-card>
                </div>
                <div class="col-lg-3 col-md-6">
                    <stats-card
                        :title="$t('platform_statement.total_fees')"
                        type="gradient-info"
                        :sub-title="formatCurrency(statement.total_platform_fee)"
                        icon="ni ni-tag"
                    >
                    </stats-card>
                </div>
                <div class="col-lg-3 col-md-6">
                    <stats-card
                        :title="$t('platform_statement.net_amount')"
                        type="gradient-green"
                        :sub-title="formatCurrency(statement.net_amount)"
                        icon="ni ni-chart-bar-32"
                    >
                    </stats-card>
                </div>
            </div>


            <!-- Tiktok -->
            <!-- Revenue Breakdown Card -->
            <div class="tiktok" v-if="statement.platform_id === 2">
            <card v-if="statement">
                <template slot="header">
                    <div class="d-flex align-items-center">
                        <i class="ni ni-money-coins text-primary mr-2"></i>
                        <h3 class="mb-0">{{ $t("platform_statement.revenue_breakdown") }}</h3>
                    </div>
                </template>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            label="Total revenue"
                            :value="formatCurrency(statement.total_revenue)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            label="Subtotal after seller discounts"
                            :value="formatCurrency(statement.subtotal_after_seller_discounts)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            label="Refund subtotal after seller discounts"
                            :value="formatCurrency(statement.refund_subtotal_after_seller_discounts)"
                            disabled
                        ></base-input>
                    </div>
                </div>
            </card>

             <card v-if="statement && hasFeeBreakdown">
                <template slot="header">
                    <div class="d-flex align-items-center">
                        <i class="ni ni-tag text-warning mr-2"></i>
                        <h3 class="mb-0">{{ $t("platform_statement.fee_breakdown") }}</h3>
                    </div>
                </template>

                <div class="row">
                    <div class="col-md-4" >
                        <base-input
                            label="Total fees"
                            :value="formatCurrency(statement.total_platform_fee)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4" >
                        <base-input
                            label="TikTok Shop commission fee"
                            :value="formatCurrency(statement.total_commission)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4" >
                        <base-input
                            label="Transaction fee"
                            :value="formatCurrency(statement.total_transaction_fee)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4" >
                        <base-input
                            label="Seller shipping fee"
                            :value="formatCurrency(statement.total_shipping_fee)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4" >
                        <base-input
                            label="Affiliate commission"
                            :value="formatCurrency(statement.total_affiliate_commission)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4" >
                        <base-input
                            label="Affiliate Shop Ads commission"
                            :value="formatCurrency(statement.total_affiliate_shop_ads)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4" >
                        <base-input
                            :label="$t('platform_statement.total_service_fees')"
                            :value="formatCurrency(statement.total_service_fees)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4" >
                        <base-input
                            :label="$t('platform_statement.total_other_fee')"
                            :value="formatCurrency(statement.total_other_fee)"
                            disabled
                        ></base-input>
                    </div>
                </div>
            </card>

            <card v-if="statement && hasAdjustmentBreakdown">
                <template slot="header">
                    <div class="d-flex align-items-center">
                        <i class="ni ni-settings text-info mr-2"></i>
                        <h3 class="mb-0">{{ $t("platform_statement.adjustment_breakdown") }}</h3>
                    </div>
                </template>

                <div class="row">
                    <div class="col-md-4" v-if="statement.total_adjustment !== 0">
                        <base-input
                            :label="$t('platform_statement.total_adjustment')"
                            :value="formatCurrency(statement.total_adjustment)"
                            disabled
                        >
                            <template slot="label">
                                <label class="form-control-label font-weight-bold">
                                    {{ $t('platform_statement.total_adjustment') }}
                                </label>
                            </template>
                        </base-input>
                    </div>
                    <div class="col-md-4" v-if="statement.shipping_fee_adjustment !== 0">
                        <base-input
                            :label="$t('platform_statement.shipping_fee_adjustment')"
                            :value="formatCurrency(statement.shipping_fee_adjustment)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4" v-if="statement.logistics_reimbursement !== 0">
                        <base-input
                            :label="$t('platform_statement.logistics_reimbursement')"
                            :value="formatCurrency(statement.logistics_reimbursement)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4" v-if="statement.platform_compensation !== 0">
                        <base-input
                            :label="$t('platform_statement.platform_compensation')"
                            :value="formatCurrency(statement.platform_compensation)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4" v-if="statement.platform_penalty !== 0">
                        <base-input
                            :label="$t('platform_statement.platform_penalty')"
                            :value="formatCurrency(statement.platform_penalty)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4" v-if="statement.other_adjustments !== 0">
                        <base-input
                            :label="$t('platform_statement.other_adjustments')"
                            :value="formatCurrency(statement.other_adjustments)"
                            disabled
                        ></base-input>
                    </div>
                </div>
            </card>

             </div>
            <!-- Shopee -->
            <div class="shopee" v-if="statement.platform_id === 3">
            <card v-if="statement">
                <template slot="header">
                    <div class="d-flex align-items-center">
                        <i class="ni ni-money-coins text-primary mr-2"></i>
                        <h3 class="mb-0">Total Revenue</h3>
                    </div>
                </template>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_revenue')"
                            :value="formatCurrency(statement.total_revenue)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.subtotal_after_seller_discounts')"
                            :value="formatCurrency(statement.subtotal_after_seller_discounts)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.subtotal_before_discounts')"
                            :value="formatCurrency(statement.subtotal_before_discounts)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.seller_discounts')"
                            :value="formatCurrency(statement.seller_discounts)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.refund_subtotal_after_seller_discounts')"
                            :value="formatCurrency(statement.refund_subtotal_after_seller_discounts)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.refund_subtotal_before_seller_discounts')"
                            :value="formatCurrency(statement.refund_subtotal_before_seller_discounts)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.refund_of_seller_discounts')"
                            :value="formatCurrency(statement.refund_of_seller_discounts)"
                            disabled
                        ></base-input>
                    </div>
                </div>
            </card>

             <card v-if="statement && hasFeeBreakdown">
                <template slot="header">
                    <div class="d-flex align-items-center">
                        <i class="ni ni-tag text-warning mr-2"></i>
                        <h3 class="mb-0">{{ $t("platform_statement.fee_breakdown") }}</h3>
                    </div>
                </template>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_platform_fee')"
                            :value="formatCurrency(statement.total_platform_fee)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_commission')"
                            :value="formatCurrency(statement.total_commission)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_transaction_fee')"
                            :value="formatCurrency(statement.total_transaction_fee)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_shipping_fee')"
                            :value="formatCurrency(statement.total_shipping_fee)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_affiliate_commission')"
                            :value="formatCurrency(statement.total_affiliate_commission)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_affiliate_shop_ads')"
                            :value="formatCurrency(statement.total_affiliate_shop_ads)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_service_fees')"
                            :value="formatCurrency(statement.total_service_fees)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_other_fee')"
                            :value="formatCurrency(statement.total_other_fee)"
                            disabled
                        ></base-input>
                    </div>
                </div>
            </card>

            <card>
                <template slot="header">
                    <div class="d-flex align-items-center">
                        <i class="ni ni-settings text-info mr-2"></i>
                        <h3 class="mb-0">{{ $t("platform_statement.adjustment_breakdown") }}</h3>
                    </div>
                </template>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_adjustment')"
                            :value="formatCurrency(statement.total_adjustment)"
                            disabled
                        >
                            <template slot="label">
                                <label class="form-control-label font-weight-bold">
                                    {{ $t('platform_statement.total_adjustment') }}
                                </label>
                            </template>
                        </base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.sp_adj_shipping_claim')"
                            :value="formatCurrency(statement.shipping_fee_adjustment)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.sp_adj_other')"
                            :value="formatCurrency(statement.logistics_reimbursement)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.sp_adj_refund')"
                            :value="formatCurrency(statement.platform_compensation)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.sp_adj_shipping')"
                            :value="formatCurrency(statement.platform_penalty)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.sp_adj_shipping')"
                            :value="formatCurrency(statement.other_adjustments)"
                            disabled
                        ></base-input>
                    </div>
                </div>
            </card>
             </div>
           

            <!-- Lazada -->
            <div class="lazada" v-if="statement.platform_id === 4">

             <card v-if="statement && hasFeeBreakdown">
                <template slot="header">
                    <div class="d-flex align-items-center">
                        <i class="ni ni-tag text-warning mr-2"></i>
                        <h3 class="mb-0">{{ $t("platform_statement.fee_breakdown") }}</h3>
                    </div>
                </template>

                <div class="row">
                    <div class="col-md-3" >
                        <base-input
                            :label="$t('platform_statement.lz_premium_package')"
                            :value="formatCurrency(statement.total_platform_fee)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-3">
                        <base-input
                            :label="$t('platform_statement.lz_commission_fee')"
                            :value="formatCurrency(statement.total_commission)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-3">
                        <base-input
                            :label="$t('platform_statement.lz_payment_service_fee')"
                            :value="formatCurrency(statement.total_transaction_fee)"
                            disabled
                        ></base-input>
                    </div>
  
          
                    <div class="col-md-3">
                        <base-input
                            :label="$t('platform_statement.lz_coupon_service_fee')"
                            :value="formatCurrency(statement.total_other_fee)"
                            disabled
                        ></base-input>
                    </div>
                </div>
            </card>

             </div>
        



        </div>
    </div>
</template>

<script>
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import StatsCard from "@/components/Cards/StatsCard";
import { Badge } from "@/components";
import swal from "sweetalert2";
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

export default {
    components: {
        RouteBreadCrumb,
        StatsCard,
        Badge
    },
    computed: {
        ...mapGetters({
            statement: "PlatformStatement/getStatement",
            isLoading: "PlatformStatement/isLoading",
            PlatformTable: "PlatformStatement/getPlatformTable"
        }),
        hasRevenueBreakdown() {
            if (!this.statement) return false;
            return this.statement.total_revenue !== 0 ||
                   this.statement.subtotal_after_seller_discounts !== 0 ||
                   this.statement.subtotal_before_discounts !== 0 ||
                   this.statement.seller_discounts !== 0 ||
                   this.statement.refund_subtotal_after_seller_discounts !== 0 ||
                   this.statement.refund_subtotal_before_seller_discounts !== 0 ||
                   this.statement.refund_of_seller_discounts !== 0;
        },
        hasFeeBreakdown() {
            if (!this.statement) return false;
            return this.statement.total_platform_fee !== 0 ||
                   this.statement.total_commission !== 0 ||
                   this.statement.total_transaction_fee !== 0 ||
                   this.statement.total_shipping_fee !== 0 ||
                   this.statement.total_affiliate_commission !== 0 ||
                   this.statement.total_affiliate_shop_ads !== 0 ||
                   this.statement.total_service_fees !== 0 ||
                   this.statement.total_other_fee !== 0;
        },
        hasAdjustmentBreakdown() {
            if (!this.statement) return false;
            return this.statement.total_adjustment !== 0 ||
                   this.statement.shipping_fee_adjustment !== 0 ||
                   this.statement.logistics_reimbursement !== 0 ||
                   this.statement.platform_compensation !== 0 ||
                   this.statement.platform_penalty !== 0 ||
                   this.statement.other_adjustments !== 0;
        }
    },
    mounted() {
        const statementId = this.$route.params.id;
        if (statementId) {
            this.getStatement(statementId);
        }
    },
    methods: {
        ...mapActions({
            getStatement: "PlatformStatement/getStatement",
            exportStatement: "PlatformStatement/exportStatement"
        }),
        handleBack() {
            this.$router.push("/backoffice/platform-statement");
        },
        async handleExport() {
            try {
                const response = await this.exportStatement(this.$route.params.id);
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `statement_${this.statement.statement_number}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                swal.fire({
                    title: this.$t("platform_statement.export_fail"),
                    icon: "error",
                    confirmButtonClass: "btn btn-danger btn-fill",
                    buttonsStyling: false
                });
            }
        },
        getPlatformName(platformId) {
            const platform = this.PlatformTable.find(p => p.id === platformId);
            return platform ? platform.name : "-";
        },
        formatCurrency(amount) {
            return "฿ " + parseFloat(amount || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },
        formatNumber(num) {
            return parseInt(num || 0).toLocaleString();
        },
        formatDate(date) {
            return date ? moment(date).format("YYYY-MM-DD") : "-";
        },
        formatDateTime(date) {
            return date ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "-";
        }
    }
};
</script>
